import React, { createContext, PropsWithChildren, useContext, useEffect, useState, useCallback } from "react";
import { ConfigProvider } from "antd";
import { getThemeConfig, type TenantTheme } from "../../config/theme";
import locale from 'antd/locale/fr_FR';

type ColorModeContextType = {
  mode: string;
  setMode: () => void;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

// Context for tenant theme (can be set from inside Refine)
export const TenantThemeContext = createContext<{
  tenantTheme: TenantTheme | undefined;
  setTenantTheme: (theme: TenantTheme | undefined) => void;
}>({
  tenantTheme: undefined,
  setTenantTheme: () => {},
});

// Custom hook to use color mode context
export const useColorMode = () => {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useColorMode must be used within a ColorModeContextProvider');
  }
  return context;
};

// Custom hook to use tenant theme context
export const useTenantThemeContext = () => {
  return useContext(TenantThemeContext);
};

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const colorModeFromLocalStorage = localStorage.getItem("colorMode");
  const isSystemPreferenceDark = window?.matchMedia(
    "(prefers-color-scheme: dark)"
  ).matches;

  const systemPreference = isSystemPreferenceDark ? "dark" : "light";
  const [mode, setMode] = useState(
    colorModeFromLocalStorage || systemPreference
  );

  const [tenantTheme, setTenantThemeState] = useState<TenantTheme | undefined>(undefined);

  // Memoize setTenantTheme to prevent infinite loops
  const setTenantTheme = useCallback((theme: TenantTheme | undefined) => {
    setTenantThemeState(theme);
  }, []);

  useEffect(() => {
    window.localStorage.setItem("colorMode", mode);
    // Set data attribute on body for CSS targeting
    document.body.setAttribute('data-theme', mode);
  }, [mode]);

  const setColorMode = () => {
    if (mode === "light") {
      setMode("dark");
    } else {
      setMode("light");
    }
  };

  return (
    <ColorModeContext.Provider
      value={{
        setMode: setColorMode,
        mode,
      }}
    >
      <TenantThemeContext.Provider
        value={{
          tenantTheme,
          setTenantTheme,
        }}
      >
        <ConfigProvider
          locale={locale}
          theme={getThemeConfig(mode as 'light' | 'dark', tenantTheme)}
        >
          {children}
        </ConfigProvider>
      </TenantThemeContext.Provider>
    </ColorModeContext.Provider>
  );
};
